import { DataGridDataCell } from '@myosh/odin-components';
import dot from 'dot-object';
import { useMemo } from 'react';
import type { CombinedFieldType } from '../../@types/forms';
import type { RecordResult } from '../../@types/records';
import { resolveFieldDisplayValue } from '../../utils/field-property-util';

interface RecordlinkBaseDataCellProps {
  records: RecordResult[];
  valuePath?: string;
  width?: number;
  type?: CombinedFieldType;
}

const RecordlinkBaseDataCell = ({ records, valuePath, width, type }: RecordlinkBaseDataCellProps) => {
  const displayValue = useMemo(() => {
    if (!valuePath) {
      return '';
    }

    const total = records?.length ?? 0;
    if (total === 0) {
      return '';
    }

    const _values: Array<string> = [];
    for (const record of records) {
      const value = dot.pick(valuePath, record);
      if (value !== undefined) {
        const finalValue = resolveFieldDisplayValue(value);
        if (finalValue) {
          _values.push(finalValue);
        }
      }
    }

    return shouldUseVerticalBarSeparator(type)
      ? _values.sort((a, b) => a.localeCompare(b)).join(' | ')
      : _values.sort((a, b) => a.localeCompare(b));
  }, [records, type, valuePath]);

  const groupAndCount = useMemo(
    () =>
      (displayValue: string | string[]): string | string[] => {
        if (Array.isArray(displayValue)) {
          const countValues: Record<string, number> = {};
          for (const value of displayValue) {
            countValues[value] = (countValues[value] || 0) + 1;
          }
          return Object.entries(countValues).map(([value, count]) => (count > 1 ? `${value} (x${count})` : value));
        }
        return displayValue;
      },
    []
  );

  if (records.length > 0) {
    return <DataGridDataCell value={groupAndCount(displayValue)} width={width} />;
  } else {
    return null;
  }
};

export default RecordlinkBaseDataCell;

const shouldUseVerticalBarSeparator = (type?: CombinedFieldType) => {
  switch (type) {
    case 'TEXTFIELD':
    case 'TEXTAREA':
    case 'MULTISELECTCHECKBOX':
    case 'MULTISELECTPERSONFIELD':
    case 'MULTISELECTFIELD':
    case 'READERS':
    case 'DROPDOWNMULTISELECT':
      return true;
    default:
      return false;
  }
};
